import { cycleOn } from "./cycle";
import { addDays, daysBetween, dayMonth, parseDay, weekKey } from "./date";
import { dayKcal } from "./energy";
import { testsInsight } from "./fitnessTests";
import { basedOn, type Insight, type Window } from "./insight";
import { beforePlan, neutralDay, sessionFor, sessionKeyFor, weekMapFor } from "./plan";
import { hoursMinutes } from "./progressView";
import { dayActive, dayFull, longestStreak } from "./stats";
import { hasTreadmill } from "./treadmill";
import type { DayLog, KayaState, PhaseIndex, SessionKey, TreadmillLog } from "./types";
import { walkSecs } from "./walk";
import { weekDone, weekRows } from "./week";

/* ---------- active time ---------- */

/**
 * Seconds actually spent exercising on a day: the guided session's clock (`elapsedMs`), the
 * rescue/extra clocks, every free walk's `secs`, and the treadmill's own minutes.
 *
 * Three older figures disagreed about this, and none was complete (inventory §2):
 * `stats.weekStats().min` summed `elapsedMs` alone plus a flat 30 min per treadmill day, so walks
 * and rescues vanished; `stats.insights()` did the same, and averaged over days with a session
 * only; `activity.moveMinutes()` had walks and `extraElapsed` but priced the treadmill from its
 * distance at a nominal 5 km/h. This is the single source for active time from now on — every
 * Progress figure reads it, and none of them may invent a duration the logs do not hold.
 */
function activeSecs(L: DayLog | undefined): number {
  if (!L) return 0;
  // A finished extra banks its clock in `extraDoneMs` and zeroes `extraElapsed`. The merge stamps
  // that reset now (ADR-034), but a day written before the stamp can still carry a finished clock
  // the old larger-value rule handed back, so the two are not summed: the larger of them is right
  // after a finish and on such a day, and only undercounts while a second extra is still running.
  const ms = (L.elapsedMs || 0) + Math.max(L.extraDoneMs || 0, L.extraElapsed || 0);
  return ms / 1000 + walkSecs(L.walks) + treadmillMins(L.treadmill) * 60;
}

/**
 * The treadmill's real duration. `mins` is written on every finish; an older log without it may
 * still carry both ends, which bound the run (pauses included, the best the log can do). With
 * neither there is no duration, and it counts 0 — never a flat 30.
 */
export function treadmillMins(t: TreadmillLog | null | undefined): number {
  if (!t) return 0;
  if (t.mins && t.mins > 0) return t.mins;
  if (t.startedAt && t.at) {
    const m = (new Date(t.at).getTime() - new Date(t.startedAt).getTime()) / 60000;
    if (Number.isFinite(m) && m > 0 && m < 300) return m;
  }
  return 0;
}

/** Minutes actually exercising on `ds`; 0 for a day with nothing logged. */
export function activeMinutes(S: KayaState, ds: string): number {
  return Math.round(activeSecs(S.logs[ds]) / 60);
}

/** Every date from `from` to `to` inclusive. */
function span(from: string, to: string): string[] {
  const out: string[] = [];
  for (let ds = from; ds <= to; ds = addDays(ds, 1)) out.push(ds);
  return out;
}

/** The days a window covers, ending today: `w` days, or since the plan's start for 0. */
export function windowDays(S: KayaState, today: string, w: Window): string[] {
  const from = w === 0 ? planStart(S, today) : addDays(today, -(w - 1));
  return span(from, today);
}

function planStart(S: KayaState, today: string): string {
  const s = (S.profile.startDate || "").slice(0, 10);
  return s && s <= today ? s : today;
}

/** Sums seconds before rounding, so a week of 40-second remainders is not lost to rounding. */
function sumMinutes(S: KayaState, days: string[]): number {
  return Math.round(days.reduce((a, ds) => a + activeSecs(S.logs[ds]), 0) / 60);
}

export function activeInWindow(S: KayaState, today: string, w: Window): number {
  return sumMinutes(S, windowDays(S, today, w));
}

/** Mean active minutes over the days in the window that had any — "38 min on a training day". */
export function averageDay(S: KayaState, today: string, w: Window = 28): number | null {
  const days = windowDays(S, today, w).filter((ds) => activeSecs(S.logs[ds]) > 0);
  if (!days.length) return null;
  return Math.round(sumMinutes(S, days) / days.length);
}

export interface WeekActive {
  weekStart: string;
  minutes: number;
  sessions: number;
  walks: number;
  treadmill: number;
}

/**
 * The first day of the plan week holding `today`. Plan weeks start on the start date's weekday, so
 * "week 12" means the same seven days here as in the header; Monday when no start is set.
 */
export function planWeekStart(S: KayaState, today: string): string {
  const start = (S.profile.startDate || "").slice(0, 10);
  if (!start) return weekKey(parseDay(today));
  const back = (parseDay(today).getDay() - parseDay(start).getDay() + 7) % 7;
  return addDays(today, -back);
}

/**
 * Active time per plan week, oldest first — the last `weeks` *complete* weeks. The running week
 * is left out because a half-finished bar reads as a drop; its figure is `weekVsLast().soFar`.
 * Weeks wholly before the plan's start are dropped rather than drawn as empty bars.
 */
export function activeByWeek(S: KayaState, today: string, weeks = 12): WeekActive[] {
  const current = planWeekStart(S, today);
  const start = (S.profile.startDate || "").slice(0, 10);
  const out: WeekActive[] = [];
  for (let i = weeks; i >= 1; i--) {
    const weekStart = addDays(current, -7 * i);
    const days = span(weekStart, addDays(weekStart, 6));
    if (start && days[6] < start) continue;
    out.push({
      weekStart,
      minutes: sumMinutes(S, days),
      sessions: days.filter((ds) => dayFull(S, ds)).length,
      walks: days.reduce((a, ds) => a + (S.logs[ds]?.walks?.length || 0), 0),
      treadmill: days.filter((ds) => !!S.logs[ds]?.treadmill).length,
    });
  }
  return out;
}

/* ---------- WHO ---------- */

export const WHO_MINUTES = 150;
export const WHO_STRENGTH_DAYS = 2;

/** The guided sessions that load muscle; P, Y and rest count toward neither WHO figure. Of the
 *  Steady templates, Strength & balance and the Chair session are strength days; Gentle cardio is
 *  aerobic, Morning joints mobility and the walk day a walk, so none of the three is (ADR-052);
 *  nor is Morning yoga & breathing, an extra that counts as active minutes only (ADR-067). */
const STRENGTH: SessionKey[] = ["A", "B", "C", "T", "SB", "CH"];

function strengthDay(S: KayaState, ds: string): boolean {
  const L = S.logs[ds];
  if (!L) return false;
  const key = L.sessionKey || sessionKeyFor(S, ds);
  if (dayFull(S, ds) && STRENGTH.includes(key)) return true;
  return (L.extras || []).some((k) => STRENGTH.includes(k) && hasTicks(L, "extra:" + k));
}

function hasTicks(L: DayLog, storeKey: string): boolean {
  return Object.keys(L.done?.[storeKey] || {}).length > 0;
}

export interface WhoMinutes {
  minutes: number;
  met: boolean;
  strengthDays: number;
  strengthMet: boolean;
}

/**
 * The last 7 days against WHO's 150 minutes and 2 strength days. The minutes are all active time,
 * guided sessions included: the guidelines' "moderate" bar is ≥ 3 METs, which the circuits and
 * intervals clear; only the gentle and cycle-care sessions sit below it, and they are short.
 */
export function whoMinutes(S: KayaState, today: string): WhoMinutes {
  const days = windowDays(S, today, 7);
  const minutes = sumMinutes(S, days);
  const strengthDays = days.filter((ds) => strengthDay(S, ds)).length;
  return {
    minutes,
    met: minutes >= WHO_MINUTES,
    strengthDays,
    strengthMet: strengthDays >= WHO_STRENGTH_DAYS,
  };
}

/* ---------- this week vs last ---------- */

/** A side is null when its window has nothing to say — nothing scheduled, or nothing logged —
 *  so a week that did not exist reads "–", never 0 (invariant 11). */
export interface Pair {
  now: number | null;
  prev: number | null;
}

export interface WeekVsLast {
  /** Monday–Sunday weeks, as the Train tab counts them: "2 of 6 so far" vs last week's. */
  sessions: { now: { done: number; of: number } | null; prev: { done: number; of: number } | null };
  /** The rest are rolling 7-day windows, so early in the week the comparison is still fair. */
  treadKm: Pair;
  work: Pair;
  active: Pair;
  /** Active minutes Monday to today. */
  soFar: number;
}

function sumOver(days: string[], f: (ds: string) => number): number {
  return days.reduce((a, ds) => a + f(ds), 0);
}

export function weekVsLast(S: KayaState, today: string): WeekVsLast {
  const now = windowDays(S, today, 7);
  const prev = windowDays(S, addDays(today, -7), 7);
  const km = (ds: string) => S.logs[ds]?.treadmill?.km || 0;
  const work = (ds: string) => dayKcal(S, ds);
  const monday = weekKey(parseDay(today));
  const week = (d: { done: number; of: number }) => (d.of ? d : null);
  const logged = (days: string[], f: (ds: string) => number, v: number) => (days.some((ds) => f(ds) > 0) ? v : null);
  const mins = (days: string[]) => {
    const m = sumMinutes(S, days);
    return m > 0 ? m : null;
  };
  return {
    sessions: {
      now: week(weekDone(weekRows(S, today))),
      prev: week(weekDone(weekRows(S, addDays(monday, -1)))),
    },
    treadKm: { now: logged(now, km, round1(sumOver(now, km))), prev: logged(prev, km, round1(sumOver(prev, km))) },
    work: { now: logged(now, work, sumOver(now, work)), prev: logged(prev, work, sumOver(prev, work)) },
    active: { now: mins(now), prev: mins(prev) },
    soFar: sumMinutes(S, span(monday, today)),
  };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/* ---------- consistency ---------- */

/**
 * A day the plan asked for a session: not a rest or period day, not a travel day (travel swaps the
 * session for a floor one the plan never scheduled), and not before the plan began.
 */
function scheduled(S: KayaState, ds: string): boolean {
  return !neutralDay(S, ds) && !beforePlan(S, ds) && !S.travelDates[ds];
}

export interface Consistency {
  pct: number;
  n: number;
  of: number;
  streak: number;
  longest: number;
}

/**
 * Scheduled days in the last 28 that were done or covered (a rescue, a freeze, a period skip).
 * Today counts only once it is done — it cannot be missed before it is over.
 */
export function consistency(S: KayaState, today: string): Consistency {
  const days = windowDays(S, today, 28).filter(
    (ds) => scheduled(S, ds) && (ds < today || covered(S, ds))
  );
  const n = days.filter((ds) => covered(S, ds)).length;
  return {
    pct: days.length ? Math.round((n / days.length) * 100) : 0,
    n,
    of: days.length,
    streak: streakOn(S, today),
    longest: longestStreak(S),
  };
}

/** `dayActive` reads the ticks; a session finished with its clock alone still counts. */
function covered(S: KayaState, ds: string): boolean {
  return dayActive(S, ds) || dayFull(S, ds);
}

/** `stats.streak` counted back from `today` instead of the wall clock, so it can be tested. */
function streakOn(S: KayaState, today: string): number {
  let n = 0;
  let ds = today;
  for (let i = 0; i < 400; i++) {
    if (covered(S, ds)) n++;
    else if (!neutralDay(S, ds) && i > 0) break;
    ds = addDays(ds, -1);
  }
  return n;
}

/* ---------- progression ---------- */

/** `plan.phase` reads the wall clock; this is the same rule for a given day. */
export function phaseOn(S: KayaState, ds: string): PhaseIndex {
  // a day that froze what it ran ran at that phase, whatever the rule says now (ADR-050)
  const ran = S.logs[ds]?.ran;
  if (ran) return ran.phase;
  const mode = S.profile.phaseMode;
  if (mode && mode !== "auto") return +mode as PhaseIndex;
  const week = Math.floor(Math.max(0, daysBetween(planStart(S, ds), ds)) / 7) + 1;
  return (week <= 2 ? 0 : week <= 4 ? 1 : 2) as PhaseIndex;
}

/**
 * The day the phase holding `today` began. A manual phase records no switch date, so the plan's
 * own start is the only defensible answer there.
 */
export function phaseStart(S: KayaState, today: string): string {
  const start = planStart(S, today);
  const mode = S.profile.phaseMode;
  if (mode && mode !== "auto") return start;
  return addDays(start, [0, 14, 28][phaseOn(S, today)]);
}

/** Days with a guided session clock, oldest first. */
function sessionDays(S: KayaState, from: string, to: string): string[] {
  return Object.keys(S.logs)
    .filter((ds) => ds >= from && ds <= to && (S.logs[ds].elapsedMs || 0) > 0)
    .sort();
}

function meanMinutes(S: KayaState, days: string[]): number {
  return days.reduce((a, ds) => a + (S.logs[ds].elapsedMs || 0), 0) / days.length / 60000;
}

/** Plank-family holds (the moves the plan lengthens by phase), low–high, at phase `p`. */
export function holdRange(keys: SessionKey[], p: PhaseIndex): [number, number] | null {
  const holds: number[] = [];
  keys.forEach((k) =>
    sessionFor(k).blocks.forEach((b) =>
      b.items.forEach((it) => {
        if (it.id.includes("plank") && it.t) holds.push(it.t[p]);
      })
    )
  );
  return holds.length ? [Math.min(...holds), Math.max(...holds)] : null;
}

export interface TreadmillRun {
  ds: string;
  km: number;
  mins: number;
  /** km/h over the whole run. The log keeps no speeds, so this is distance over time. */
  pace: number;
  console: boolean;
}

/** Treadmill runs with a distance and a duration, oldest first. */
export function treadmillRuns(S: KayaState, today: string): TreadmillRun[] {
  return Object.keys(S.logs)
    .filter((ds) => ds <= today)
    .sort()
    .flatMap((ds) => {
      const t = S.logs[ds].treadmill;
      const mins = treadmillMins(t);
      if (!t || !(t.km > 0) || !(mins > 0)) return [];
      return [{ ds, km: t.km, mins: Math.round(mins), pace: round1(t.km / (mins / 60)), console: !!t.kmFromConsole }];
    });
}

export interface Progression {
  phase: PhaseIndex;
  phaseStart: string;
  /** mean guided-session minutes: the last 7 after the phase's first week, and that first week */
  recentMin: number | null;
  firstWeekMin: number | null;
  recentN: number;
  firstWeekN: number;
  /** recent − first week, whole minutes; null until both sides have 3 sessions */
  diff: number | null;
  /** plank holds when guided sessions began vs now; null when the phase has not changed since */
  holds: { from: [number, number]; to: [number, number] } | null;
  /** the guided sessions the sentence rests on: the phase's, or every one when it compares holds */
  sessions: number;
  /** the first of those sessions, or the phase's start when there are none */
  sessionsSince: string;
  treadmill: {
    runs: TreadmillRun[];
    firstPace: number | null;
    lastPace: number | null;
    last: TreadmillRun | null;
    /**
     * The brisk speed the protocol asks for now. There is no "was": the log stores no speeds and
     * the profile keeps only the current setting, so a past brisk speed cannot be recovered —
     * whole-run pace (km over minutes) is the comparison the data supports.
     */
    briskNow: number;
  };
}

export function progression(S: KayaState, today: string): Progression {
  const p = phaseOn(S, today);
  const from = phaseStart(S, today);
  const firstWeek = sessionDays(S, from, addDays(from, 6));
  const recent = sessionDays(S, addDays(from, 7), today).slice(-7);
  const enough = firstWeek.length >= 3 && recent.length >= 3;
  const recentMin = recent.length ? meanMinutes(S, recent) : null;
  const firstWeekMin = firstWeek.length ? meanMinutes(S, firstWeek) : null;

  const all = sessionDays(S, "0000-00-00", today);
  const keys = Array.from(
    new Set(all.map((ds) => S.logs[ds].sessionKey || sessionKeyFor(S, ds)))
  ).filter((k) => STRENGTH.includes(k));
  const began = all.length ? phaseOn(S, all[0]) : p;
  const fromHold = holdRange(keys, began);
  const toHold = holdRange(keys, p);
  const holds =
    fromHold && toHold && (fromHold[0] !== toHold[0] || fromHold[1] !== toHold[1])
      ? { from: fromHold, to: toHold }
      : null;

  // The evidence names the span the verdict actually used: holds reach back to the first session
  // ever, the length comparison only to the phase's start. Count and date come from one list.
  const counted = holds && enough ? all : all.filter((ds) => ds >= from);
  const runs = treadmillRuns(S, today);
  return {
    phase: p,
    phaseStart: from,
    recentMin: recentMin === null ? null : Math.round(recentMin),
    firstWeekMin: firstWeekMin === null ? null : Math.round(firstWeekMin),
    recentN: recent.length,
    firstWeekN: firstWeek.length,
    diff: enough && recentMin !== null && firstWeekMin !== null ? Math.round(recentMin - firstWeekMin) : null,
    holds,
    sessions: counted.length,
    sessionsSince: counted.length ? counted[0] : from,
    treadmill: {
      runs,
      firstPace: runs.length ? runs[0].pace : null,
      lastPace: runs.length ? runs[runs.length - 1].pace : null,
      last: runs.length ? runs[runs.length - 1] : null,
      briskNow: p === 0 ? S.profile.brisk1 : S.profile.brisk,
    },
  };
}

/* ---------- best day ---------- */

const DOW_PLURAL = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];

export interface BestDay {
  dow: number;
  name: string;
  done: number;
  of: number;
  /** modal start hour of the window's sessions; null with fewer than 3 timed sessions */
  hour: number | null;
}

/** Research T7: a weekday pattern means nothing on fewer sessions than this. */
const BEST_DAY_MIN = 12;

/**
 * The weekday with the highest done-ratio over the last 5 weeks (ties to the one with more done),
 * and the hour sessions most often started — `completedAt` less the session clock.
 */
export function bestDay(S: KayaState, today: string): BestDay | null {
  const days = span(addDays(today, -34), today).filter((ds) => ds < today || dayFull(S, ds));
  const done = (ds: string) => dayFull(S, ds) || !!S.logs[ds]?.rescued;
  if (days.filter(done).length < BEST_DAY_MIN) return null;

  let best: BestDay | null = null;
  for (let dow = 0; dow < 7; dow++) {
    const sched = days.filter((ds) => parseDay(ds).getDay() === dow && scheduled(S, ds));
    if (sched.length < 3) continue;
    const n = sched.filter(done).length;
    const better =
      !best || n / sched.length > best.done / best.of ||
      (n / sched.length === best.done / best.of && n > best.done);
    if (better) best = { dow, name: DOW_PLURAL[dow], done: n, of: sched.length, hour: null };
  }
  if (!best) return null;

  const hours = new Array<number>(24).fill(0);
  let timed = 0;
  days.forEach((ds) => {
    const L = S.logs[ds];
    if (!L?.completedAt || !L.elapsedMs) return;
    const t = new Date(new Date(L.completedAt).getTime() - L.elapsedMs);
    if (Number.isNaN(t.getTime())) return;
    hours[t.getHours()]++;
    timed++;
  });
  if (timed >= 3) best.hour = hours.indexOf(Math.max(...hours));
  return best;
}

/** "mornings 7–8" */
export function hourPhrase(h: number): string {
  const part = h < 12 ? "mornings" : h < 17 ? "afternoons" : "evenings";
  return `${part} ${h}–${h + 1}`;
}

/* ---------- rest and overreach ---------- */

/** Trained on the day: a session, a rescue or extra, or the treadmill. A walk is still rest. */
function trainedOn(S: KayaState, ds: string): boolean {
  const L = S.logs[ds];
  if (!L) return false;
  return (
    !!L.completedAt ||
    !!L.treadmill ||
    (L.elapsedMs || 0) > 0 ||
    (L.extraElapsed || 0) > 0 ||
    (L.extraDoneMs || 0) > 0 ||
    Object.values(L.done || {}).some((o) => Object.keys(o).length > 0)
  );
}

/** The plan's rest days in the last 4 weeks (before today), and how many were rested. */
export function restAdherence(S: KayaState, today: string): { n: number; of: number } {
  const rest = span(addDays(today, -28), addDays(today, -1)).filter(
    (ds) => weekMapFor(S)[parseDay(ds).getDay()] === "R" && !beforePlan(S, ds)
  );
  return { n: rest.filter((ds) => !trainedOn(S, ds)).length, of: rest.length };
}

export interface Overreach {
  /** week-on-week rises of 20 % or more among the last 5 complete weeks */
  rises: number;
  twiceInARow: boolean;
  weeks: WeekActive[];
}

export function overreachDetail(S: KayaState, today: string): Overreach {
  const weeks = activeByWeek(S, today, 5);
  let rises = 0;
  let run = 0;
  let twiceInARow = false;
  for (let i = 1; i < weeks.length; i++) {
    const a = weeks[i - 1].minutes;
    // A rise from nothing is a start, not a jump.
    const rose = a > 0 && weeks[i].minutes >= a * 1.2;
    if (rose) rises++;
    run = rose ? run + 1 : 0;
    if (run >= 2) twiceInARow = true;
  }
  return { rises, twiceInARow, weeks };
}

/** Active time rose 20 % or more two weeks running. The plan's framing, not a diagnosis: no HR. */
export function overreach(S: KayaState, today: string): boolean {
  return overreachDetail(S, today).twiceInARow;
}

/* ---------- lifetime ---------- */

export interface Lifetime {
  since: string;
  sessions: number;
  treadmill: number;
  walks: number;
  minutes: number;
  km: number;
  work: number;
}

export function lifetime(S: KayaState, today: string): Lifetime {
  const days = Object.keys(S.logs).filter((ds) => ds <= today);
  let km = 0;
  let walks = 0;
  days.forEach((ds) => {
    const L = S.logs[ds];
    km += L.treadmill?.km || 0;
    (L.walks || []).forEach((w) => {
      walks++;
      // A distance she vouched for beats one derived from the stride.
      km += w.kmDeclared ?? w.km ?? 0;
    });
  });
  return {
    since: planStart(S, today),
    sessions: days.filter((ds) => dayFull(S, ds)).length,
    treadmill: days.filter((ds) => !!S.logs[ds].treadmill).length,
    walks,
    minutes: sumMinutes(S, days),
    km: Math.round(km),
    work: days.reduce((a, ds) => a + dayKcal(S, ds), 0),
  };
}

/* ---------- insights ---------- */

function plural(n: number, one: string, many = one + "s"): string {
  return `${n} ${n === 1 ? one : many}`;
}

function moreDays(n: number): string {
  return `after ${plural(n, "more day")}`;
}

/** Research §2.3: under a week of plan, nothing here has earned a sentence yet. */
const PLAN_MIN_DAYS = 7;

export function trainingInsights(S: KayaState, today: string): Insight[] {
  const planDays = daysBetween(planStart(S, today), today) + 1;
  const young = planDays < PLAN_MIN_DAYS;
  const left = PLAN_MIN_DAYS - planDays;
  const out: Insight[] = [];

  // T1 — the minutes the app always had and never showed.
  const life = lifetime(S, today);
  const lifeBasis = basedOn(
    plural(life.sessions, "session"),
    plural(life.treadmill, "treadmill run"),
    plural(life.walks, "walk")
  );
  out.push(
    life.minutes > 0
      ? {
          key: "training.active",
          text: `You have exercised ${hoursMinutes(life.minutes)} since ${dayMonth(life.since)} — every session, walk and treadmill run counted.`,
          basedOn: lifeBasis,
        }
      : {
          key: "training.active",
          text: "Active time adds up from your first session, walk or treadmill run.",
          basedOn: lifeBasis,
          wait: "after your first session",
        }
  );

  // T2 — WHO minutes and strength days.
  const who = whoMinutes(S, today);
  out.push(
    young
      ? {
          key: "training.who",
          text: "WHO's 150 minutes and 2 strength days are a weekly measure; they compare after a full week.",
          basedOn: basedOn(plural(planDays, "day"), "7 days"),
          wait: moreDays(left),
        }
      : {
          key: "training.who",
          text:
            (who.met
              ? `${who.minutes} active minutes in the last 7 days — past WHO's 150.`
              : `${who.minutes} of WHO's 150 active minutes in the last 7 days.`) +
            ` Strength on ${plural(who.strengthDays, "day")} — WHO asks for 2.`,
          basedOn: "last 7 days · sessions, walks and treadmill",
        }
  );

  // T3 — consistency, led by the best day once there is a pattern to name.
  const c = consistency(S, today);
  const best = young ? null : bestDay(S, today);
  if (young) {
    out.push({
      key: "training.consistency",
      text: "Consistency is the share of scheduled sessions done; it means something after a week.",
      basedOn: basedOn(plural(planDays, "day"), "28 days"),
      wait: moreDays(left),
    });
  } else if (best) {
    out.push({
      key: "training.consistency",
      text:
        `${best.name} go best: ${best.done} of ${best.of} done` +
        (best.hour !== null ? `, ${hourPhrase(best.hour)}.` : "."),
      basedOn: basedOn("the last 5 weeks", best.hour !== null ? "session start times" : null),
    });
  } else {
    out.push({
      key: "training.consistency",
      text: `${c.pct} % of scheduled sessions done in 28 days; ${plural(c.streak, "day")} streak.`,
      basedOn: basedOn(`${c.n} of ${c.of} scheduled`, "28 days"),
    });
  }

  // R1 — rest days kept.
  const rest = restAdherence(S, today);
  if (young || rest.of < 2) {
    out.push({
      key: "training.rest",
      text: "Whether you keep the plan's rest day shows after two of them.",
      basedOn: basedOn(plural(rest.of, "rest day"), "4 weeks"),
      wait: `after ${plural(Math.max(1, 2 - rest.of), "more rest day")}`,
    });
  } else {
    const weeks = plural(rest.of, "week");
    out.push({
      key: "training.rest",
      text:
        rest.n === rest.of
          ? `You took the plan's rest day every week for the last ${weeks}.`
          : rest.n === 0
            ? `You trained through the plan's rest day in each of the last ${weeks}. A rest day is part of the plan.`
            : `You took the plan's rest day ${rest.n} of the last ${weeks}.`,
      basedOn: weeks,
    });
  }

  // T4 — session length within the phase; holds across phases.
  const pr = progression(S, today);
  const phaseNo = pr.phase + 1;
  const guided = basedOn(`${plural(pr.sessions, "guided session")} since ${dayMonth(pr.sessionsSince)}`);
  if (young || pr.diff === null) {
    const need = Math.max(3 - pr.firstWeekN, 0) + Math.max(3 - pr.recentN, 0);
    out.push({
      key: "training.progression",
      text: `Session length compares with your first week of phase ${phaseNo} once both have 3 sessions.`,
      basedOn: guided,
      wait: `after ${plural(Math.max(need, 1), "more session")}`,
    });
  } else {
    const d = pr.diff;
    const len =
      d === 0
        ? `Sessions run as long as in your first week of phase ${phaseNo}`
        : `Sessions run ${Math.abs(d)} min ${d > 0 ? "longer" : "shorter"} than in your first week of phase ${phaseNo}`;
    const holds = pr.holds
      ? ` and holds went from ${secsRange(pr.holds.from)} to ${secsRange(pr.holds.to)}`
      : "";
    out.push({ key: "training.progression", text: `${len}${holds}.`, basedOn: guided });
  }

  // T5 — treadmill pace, only for someone who has one.
  if (hasTreadmill(S)) {
    const runs = pr.treadmill.runs;
    const runBasis = runs.length
      ? basedOn(`${plural(runs.length, "run")} since ${dayMonth(runs[0].ds)}`)
      : basedOn("0 runs");
    const tag = runs.length && runs.every((r) => r.console) ? "console" : "est";
    const last = pr.treadmill.last;
    if (runs.length < 3 || !last || pr.treadmill.firstPace === null) {
      out.push({
        key: "training.treadmill",
        text: "Treadmill pace compares first run with latest after 3 runs.",
        basedOn: runBasis,
        tag,
        wait: `after ${plural(3 - runs.length, "more run")}`,
      });
    } else {
      const a = pr.treadmill.firstPace;
      const b = last.pace;
      const pace =
        a === b
          ? `Treadmill pace is steady at ${b.toFixed(1)} km/h`
          : `Treadmill pace is ${b > a ? "up" : "down"} from ${a.toFixed(1)} to ${b.toFixed(1)} km/h`;
      out.push({
        key: "training.treadmill",
        text: `${pace}; the last run was ${last.km.toFixed(1)} km in ${last.mins} min.`,
        basedOn: runBasis,
        tag,
      });
    }
  }

  // T8 — the quiet form of the overreach signal.
  const o = overreachDetail(S, today);
  const link = cycleOn(S) ? { to: "/progress/cycle", label: "Energy on period days" } : undefined;
  const weeksBasis = `${plural(o.weeks.length, "week")} of active time`;
  if (o.weeks.length < 3) {
    out.push({
      key: "training.overreach",
      text: "A jump in training load shows once there are three full weeks to compare.",
      basedOn: weeksBasis,
      wait: `after ${plural(3 - o.weeks.length, "more week")}`,
      ...(link ? { link } : {}),
    });
  } else {
    out.push({
      key: "training.overreach",
      text: o.twiceInARow
        ? "Active time rose 20 % or more two weeks running. A rest day is part of the plan."
        : o.rises === 0
          ? "No overreach signal: active time has not risen 20 % week on week."
          : o.rises === 1
            ? "No overreach signal: active time rose 20 % week on week once, never twice."
            : `No overreach signal: active time rose 20 % week on week ${o.rises} times, never twice in a row.`,
      basedOn: weeksBasis,
      ...(link ? { link } : {}),
    });
  }

  // T9 — the self-tests: the progress a parent can see, the retest beside it (ADR-054)
  const tests = testsInsight(S, today, parseDay(today));
  if (tests) out.push(tests);

  return out;
}

function secsRange([a, b]: [number, number]): string {
  return a === b ? `${a} s` : `${a}–${b} s`;
}
